import {
  type ClearPersonalComposer,
  type ComposerReply,
  type DraftKey,
  LEAD_REPLY,
  type PersonalComposerRead,
  PersonalComposerReadSchema,
  PersonalComposerReadWithReplySchema,
  type PersonalComposerWrite,
  PersonalComposerWriteSchema,
  type SaveComposerFull,
} from "../../src/personal-composer-contract.ts";

export class ComposerRequestError extends Error {
  constructor(readonly code: string) {
    super(code);
  }
}

/** A read, with the whole reply where the server keeps one (absent from an older server). */
export type ComposerHead = PersonalComposerRead & { reply?: ComposerReply | null };

export type PersonalComposerClient = {
  read(): Promise<ComposerHead>;
  save(input: SaveComposerFull): Promise<PersonalComposerWrite>;
  clear(input: ClearPersonalComposer): Promise<PersonalComposerWrite>;
};

async function responseBody(response: Response): Promise<unknown> {
  const body: unknown = await response.json();
  if (response.ok) return body;
  const code =
    typeof body === "object" && body !== null && "error" in body && typeof body.error === "string"
      ? body.error
      : "request_failed";
  // A generic 404 is a failed read. Only the explicit rollout response permits local-only drafts.
  throw new ComposerRequestError(
    response.status === 404 && code === "mcp_disabled"
      ? code
      : code === "mcp_disabled"
        ? "request_failed"
        : code,
  );
}

/**
 * One box's personal draft (LEA-513 C8). The reply to the lead names no target, so its address is
 * the one every earlier tab used; another box names its target and kind, which the server refuses
 * until LEA-514 gives the app one box per message.
 */
export function personalComposerClient(
  workspaceId: string,
  planId: string,
  draft: DraftKey = LEAD_REPLY,
): PersonalComposerClient {
  const base = `/api/work/workspaces/${encodeURIComponent(workspaceId)}/plans/${encodeURIComponent(planId)}/composer`;
  const box =
    draft.target === LEAD_REPLY.target && draft.kind === LEAD_REPLY.kind
      ? ""
      : `target=${encodeURIComponent(draft.target)}&kind=${encodeURIComponent(draft.kind)}`;
  const path = box ? `${base}?${box}` : base;
  const write = async (method: "PUT" | "DELETE", input: SaveComposerFull | ClearPersonalComposer) =>
    PersonalComposerWriteSchema.parse(
      await responseBody(
        await fetch(path, {
          method,
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        }),
      ),
    );
  return {
    read: async () => {
      const body = await responseBody(
        await fetch(`${base}?reply=1${box ? `&${box}` : ""}`, {
          credentials: "same-origin",
          cache: "no-store",
        }),
      );
      // An older server ignores `reply=1` and answers the plain read.
      const whole = PersonalComposerReadWithReplySchema.safeParse(body);
      return whole.success ? whole.data : PersonalComposerReadSchema.parse(body);
    },
    save: (input) => write("PUT", input),
    clear: (input) => write("DELETE", input),
  };
}
